import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { theme } from '../../../../theme/theme'
import { DropdownSelect } from '../../../common/DropdownSelect'
import { FormControl } from '../../../common/FormControl'
import { unique } from '../../../../helpers/array'
import { UserAvatar } from '../../../common/Avatar'
import { PreApprovedListItem } from './PreApprovedListItem'
import { useHouseholdMembersPreApproved } from './useHouseholdMembersPreApproved'
import { AddPerson } from './AddPerson'
import { ErrorView } from '../../../../views/error/ErrorView'
import { IllustrationChoices } from '../../../common/Illustration'
import { useAuth } from '../../../../hooks/useAuth'
import { unitQueries } from '../../../../api/units'
import { societyQueries } from '../../../../api/societies'
import { getUserFullName } from '../../../../helpers/user'
import { i18nSocietyType } from '../../../../i18n'

export const ManageHouseholdModalContent = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()

  const { data: _units } = useQuery(unitQueries.getUserUnits(userId))
  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const units = _units || []
  const unitIds = units.map((_unit) => _unit.societyId)

  const uniqueSocietyIds = unique(unitIds)

  const societiesWithAssignedUnit =
    userSocieties?.filter((society) =>
      uniqueSocietyIds.includes(society._id)
    ) || []

  const [selectedSocietyId, setSelectedSocietyId] = useState(
    societiesWithAssignedUnit[0]?._id
  )

  const { householdUsers, preApprovedUsers } =
    useHouseholdMembersPreApproved(selectedSocietyId)

  const societyOptions = societiesWithAssignedUnit.map((_society) => ({
    value: _society._id,
    label: _society.name,
  }))
  const selectedSociety = societiesWithAssignedUnit.find(
    (_society) => _society._id === selectedSocietyId
  )

  /*
  We have some room for improvement here for users with multiple units in a society.
  This will not support that. 

  I'm copying the behaviour from the current app, and we can improve this in the future.
  */
  const selectedUnit = units.find(
    (_unit) => _unit.societyId === selectedSocietyId
  )

  if (societyOptions.length === 0) {
    return (
      <ErrorView
        title={translate('manageHousehold.errorViews.noSociety.title', {
          context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
        })}
        subtitle={translate('manageHousehold.errorViews.noSociety.subtitle', {
          context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
        })}
        illustration={IllustrationChoices.EMPTY}
        className="mb-10"
      />
    )
  }

  return (
    <>
      <div className="flex flex-col">
        <p style={theme.textVariants.lead}>
          {translate('manageHousehold.title')}
        </p>
        <p style={theme.textVariants.base}>
          {translate('manageHousehold.subtitle', {
            context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
          })}
        </p>
        <FormControl
          label={translate('common.form.labels.society', {
            context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
          })}
          name="society"
        >
          <DropdownSelect
            value={selectedSocietyId}
            options={societyOptions}
            dropdownWidth="w-64"
            onChange={setSelectedSocietyId}
          />
        </FormControl>
        <div className="mt-6 mb-4 border-b" />
        <p style={theme.textVariants.lead} className="mb-6">
          {translate('manageHousehold.form.headers.household')}
        </p>
        <div className="flex flex-col gap-4">
          {householdUsers &&
            householdUsers.map((_user) => (
              <div key={_user._id} className="flex items-center">
                <UserAvatar user={_user} />
                <p
                  style={theme.textVariants.baseBold}
                  className="ml-2"
                  translate="no"
                >
                  {getUserFullName(_user)}
                </p>
              </div>
            ))}
        </div>
        {selectedUnit && selectedSocietyId && (
          <AddPerson unitId={selectedUnit._id} societyId={selectedSocietyId} />
        )}
        {preApprovedUsers && preApprovedUsers.length > 0 && (
          <div className="mb-6">
            <div className="mt-6 mb-4 border-b" />
            <p style={theme.textVariants.lead} className="mb-4">
              {translate('manageHousehold.form.headers.pendingInvites')}
            </p>
            <div className="flex flex-col gap-4">
              {preApprovedUsers.map((preApproved) => (
                <PreApprovedListItem
                  societyId={selectedSocietyId}
                  key={preApproved._id}
                  preApproved={preApproved}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
